import React, { useState, useEffect } from 'react';
import MaterialTable from "material-table";
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import moment from 'moment/min/moment-with-locales';
import { colors } from '../components/Theme/WebTheme';
import { SECONDORY_COLOR, MAIN_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import { ThemeProvider } from '@mui/material/styles';
import theme from "styles/tableStyle";
import AlertDialog from "../components/AlertDialog";
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';

const Campaigns = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector(state => state.settingsdata.settings);
  const campaigndata = useSelector(state => state.campaigndata);
  const { fetchCampaigns, editCampaign, sendCampaign, fetchUsersOnce } = api;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });
  const [sendDialog, setSendDialog] = useState({ open: false, row: null });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    dispatch(fetchCampaigns());
  }, [dispatch, fetchCampaigns]);

  useEffect(() => {
    setData(campaigndata.campaigns || []);
  }, [campaigndata.campaigns]);

  const segmentLabel = (rowData) => {
    const userType = rowData.usertype && rowData.usertype !== 'All' ? t(rowData.usertype) : t('all');
    const device = rowData.devicetype && rowData.devicetype !== 'All' ? rowData.devicetype : t('all');
    return `${userType} / ${device}`;
  };

  const columns = [
    { title: t('campaign_name'), field: 'name', editable: 'never' },
    { title: t('title'), field: 'title', editable: 'never' },
    { title: t('segment'), field: 'usertype', editable: 'never', render: segmentLabel },
    {
      title: t('status'), field: 'status', editable: 'never',
      render: rowData => t('campaign_status_' + (rowData.status || 'draft'))
    },
    {
      title: t('campaign_accepted'), field: 'stats.acceptedCount', editable: 'never',
      render: rowData => rowData.stats
        ? `${rowData.stats.acceptedCount || 0} / ${rowData.stats.validTokenCount || 0}`
        : '-'
    },
    {
      title: t('campaign_last_sent'), field: 'lastSentAt', defaultSort: 'desc', editable: 'never',
      render: rowData => rowData.lastSentAt ? moment(rowData.lastSentAt).format('lll') : '-'
    },
  ];

  const confirmSend = async (confirmed) => {
    const row = sendDialog.row;
    setSendDialog({ open: false, row: null });
    if (!confirmed || !row) return;

    if (!settings.AllowCriticalEditsAdmin) {
      setCommonAlert({ open: true, msg: t('demo_mode') });
      return;
    }

    setSending(true);
    // sendNotification segments from the users already in the store, so make sure they
    // are loaded before a campaign goes out or it would match nobody.
    await dispatch(fetchUsersOnce());
    const result = await dispatch(sendCampaign(row));
    setSending(false);

    setCommonAlert({
      open: true,
      msg: result?.ok
        ? t('notification_send_summary', {
            matched: result.matchedUserCount ?? 0,
            tokens: result.validTokenCount ?? 0,
            accepted: result.successCount ?? 0,
            failed: result.errorCount ?? 0,
          })
        : (result?.message || t('update_failed'))
    });
  };

  if (campaigndata.loading || sending) return <CircularLoading />;

  return (
    <ThemeProvider theme={theme}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: isRTL === 'rtl' ? 'flex-start' : 'flex-end' }}>
        <Button
          style={{
            borderRadius: "19px",
            backgroundColor: MAIN_COLOR,
            minHeight: 44,
            paddingLeft: 24,
            paddingRight: 24,
          }}
          variant="contained"
          onClick={() => navigate('/campaigns/editcampaign')}
        >
          <Typography style={{ color: colors.WHITE, fontSize: 15, fontFamily: FONT_FAMILY }}>
            {t('add_campaign')}
          </Typography>
        </Button>
      </div>
      <MaterialTable
        title={t('campaigns_title')}
        columns={columns}
        style={{
          direction: isRTL === "rtl" ? "rtl" : "ltr",
          borderRadius: "8px",
          boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
          padding: "20px",
        }}
        data={data}
        onRowClick={((evt, selected) => setSelectedRow(selected.tableData.id))}
        options={{
          pageSize: 10,
          pageSizeOptions: [10, 15, 20],
          exportButton: { csv: settings.AllowCriticalEditsAdmin, pdf: false },
          rowStyle: (rowData) => ({
            backgroundColor: selectedRow === rowData.tableData.id ? colors.ROW_SELECTED : colors.WHITE
          }),
          headerStyle: {
            position: "sticky",
            top: "0px",
            fontSize: "0.8em",
            fontWeight: 'bold ',
            color: colors.BLACK,
            backgroundColor: SECONDORY_COLOR,
            textAlign: "center",
            border: `1px solid ${colors.TABLE_BORDER}`,
            paddingRight: "20px",
            paddingLeft: "20px",
          },
          cellStyle: {
            border: `1px solid ${colors.TABLE_BORDER}`,
            textAlign: "center",
          },
          actionsColumnIndex: -1,
        }}
        localization={{
          toolbar: {
            searchPlaceholder: (t('search')),
            exportTitle: (t('export')),
          },
          header: { actions: (t('actions')) },
          body: { deleteTooltip: t('delete'), editRow: { deleteText: t('delete_message') } },
          pagination: {
            labelDisplayedRows: ('{from}-{to} ' + (t('of')) + ' {count}'),
            firstTooltip: (t('first_page_tooltip')),
            previousTooltip: (t('previous_page_tooltip')),
            nextTooltip: (t('next_page_tooltip')),
            lastTooltip: (t('last_page_tooltip'))
          },
        }}
        editable={{
          onRowDelete: settings.AllowCriticalEditsAdmin
            ? async (oldData) => { dispatch(editCampaign(oldData, "Delete")); }
            : undefined
        }}
        actions={[
          rowData => ({
            icon: 'edit',
            tooltip: t('edit'),
            onClick: (event, row) => navigate('/campaigns/editcampaign/' + row.id)
          }),
          rowData => ({
            icon: 'send',
            tooltip: t('send_campaign'),
            onClick: (event, row) => setSendDialog({ open: true, row })
          }),
        ]}
      />
      <Dialog open={sendDialog.open} onClose={() => confirmSend(false)}>
        <DialogTitle>{t('send_campaign')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('send_campaign_confirm', {
              name: sendDialog.row ? (sendDialog.row.name || sendDialog.row.title || '') : '',
              segment: sendDialog.row ? segmentLabel(sendDialog.row) : ''
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => confirmSend(false)}>{t('cancel')}</Button>
          <Button onClick={() => confirmSend(true)} style={{ color: MAIN_COLOR }}>
            {t('send_campaign')}
          </Button>
        </DialogActions>
      </Dialog>
      <AlertDialog open={commonAlert.open} onClose={() => setCommonAlert({ open: false, msg: "" })}>
        {commonAlert.msg}
      </AlertDialog>
    </ThemeProvider>
  );
}

export default Campaigns;
